import React, { useContext, useState } from "react";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "motion/react";
import Hamburger from "hamburger-react";

import { ThemeContext } from "../themeProvider";

interface Menu {
  id: number;
  name: string;
  route: string;
}

const Navbar = () => {
  const { dispatch, state } = useContext(ThemeContext);
  const [toggle, setToggle] = useState(false);

  const darkMode = state.darkMode;
  console.log("Navbar darkMode:", darkMode);

  const links: Menu[] = [
    { id: 1, name: "Home", route: "/" },
    { id: 2, name: "About", route: "about" },
    { id: 3, name: "Experience", route: "experience" },
    { id: 4, name: "Contact", route: "contact" },
  ];

  function toggleTheme() {
    if (darkMode === true) {
      dispatch({ type: "LIGHTMODE" });
    } else {
      dispatch({ type: "DARKMODE" });
    }
  }

  return (
    <>
      <nav
        className={`
          z-50 md:px-8 px-1 fixed w-full top-0 transition-all duration-300 backdrop-blur-sm
          ${darkMode
            ? " bg-gray-900/95 border-b border-gray-800 shadow-xl"
            : " bg-white/95 border-b border-gray-200 shadow-md"
          }`}
      >
        <div className="flex justify-between items-center py-2 md:py-4 md:px-2 pl-2 mx-auto">
          <div className="flex items-center cursor-pointer">
            <a
              href="/"
              className={`
                text-xl font-medium text-decoration-none whitespace-nowrap
              ${darkMode
                  ? " text-white"
                  : " text-black"
                }`}
            >
              <img src="/images/logo-small-1.png" alt="Logo" className="object-contain w-24 h-16" />
            </a>
          </div>
          <div className="hidden justify-between items-center w-full md:flex md:w-auto ">
            <ul
              className={
                "flex flex-col mt-4 md:flex-row md:space-x-8 md:mt-0 md:text-md md:font-medium"
              }
            >
              {links.map((el) => (
                <li key={el.id} className="cursor-pointer">
                  <Link
                    to={el.route}
                    activeClass="text-blue-500 font-bold"
                    spy={true}
                    smooth={true}
                    offset={-80}
                    className={`
                      block py-2 px-4 rounded-lg transition-all duration-200 cursor-pointer
                    ${darkMode
                        ? " text-gray-300 hover:text-white hover:bg-gray-800"
                        : " text-gray-700 hover:text-blue-600 hover:bg-gray-100"
                      }`}
                  >
                    {el.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div onClick={() => toggleTheme()}>
              {darkMode ? (
                <img
                  src="https://img.icons8.com/external-flaticons-flat-flat-icons/64/000000/external-sun-lighting-flaticons-flat-flat-icons.png"
                  className="w-6 ml-6 cursor-pointer hover:scale-1.50 block"
                  alt="Switch to light mode"
                />
              ) : (
                <img
                  src="https://img.icons8.com/external-prettycons-lineal-color-prettycons/49/000000/external-moon-astrology-and-symbology-prettycons-lineal-color-prettycons.png"
                  className="w-6 ml-6 cursor-pointer hover:scale-1.50 block"
                  alt="Switch to dark mode"
                />
              )}
            </div>
          </div>

          <div className="flex md:hidden items-center">
            <div onClick={() => toggleTheme()}>
              {darkMode ? (
                <img
                  src="https://img.icons8.com/external-flaticons-flat-flat-icons/64/000000/external-sun-lighting-flaticons-flat-flat-icons.png"
                  className="w-6 mr-4 cursor-pointer hover:scale-1.50 block"
                  alt="Switch to light mode"
                />
              ) : (
                <img
                  src="https://img.icons8.com/external-prettycons-lineal-color-prettycons/49/000000/external-moon-astrology-and-symbology-prettycons-lineal-color-prettycons.png"
                  alt="Switch to dark mode"
                  className="w-6 mr-4 cursor-pointer hover:scale-1.50 block"
                />
              )}
            </div>

            <Hamburger
              toggled={toggle}
              size={22}
              duration={0.8}
              distance={"lg"}
              toggle={setToggle}
              color={darkMode ? "#ffffff" : "#000000"}
            />
          </div>
        </div>
        {/* Mobile view nav bar */}
      </nav>
      <AnimatePresence>
        {toggle && (
          <>
            {/* Overlay backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setToggle(false)}
            />
            
            {/* Mobile menu */}
            <motion.div
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1, transition: { type: "spring", damping: 25, stiffness: 200 } }}
              exit={{ x: 300, opacity: 0, transition: { duration: 0.2 } }}
              className={`
                fixed top-20 right-4 z-50 w-64 rounded-2xl shadow-2xl overflow-hidden
                ${darkMode
                  ? "bg-gray-900 border border-gray-800"
                  : "bg-white border border-gray-200"
                }`}
            >
              <div className="p-4">
                <ul className="space-y-2">
                  {links.map((el, index) => (
                    <motion.li
                      key={el.id}
                      initial={{ x: 50, opacity: 0 }}
                      animate={{ 
                        x: 0, 
                        opacity: 1,
                        transition: { delay: index * 0.1 }
                      }}
                    >
                      <Link
                        to={el.route}
                        activeClass="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                        spy={true}
                        smooth={true}
                        offset={-80}
                        onClick={() => setToggle(false)}
                        className={`
                          block px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 cursor-pointer
                          ${darkMode
                            ? "text-gray-300 hover:bg-gray-800 hover:text-white"
                            : "text-gray-700 hover:bg-gray-100 hover:text-blue-600"
                          }`}
                      >
                        {el.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
                
                {/* Divider */}
                <div className={`my-4 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`} />
                
                {/* Theme toggle in menu */}
                <button
                  onClick={() => {
                    toggleTheme();
                    setToggle(false);
                  }}
                  className={`
                    w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200
                    ${darkMode
                      ? "text-gray-300 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                    }`}
                >
                  <span className="font-medium">Theme</span>
                  <span className="text-2xl">{darkMode ? '☀️' : '🌙'}</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
